import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import whatsappIcon from '../../assets/images/wats.png';
import telegramIcon from '../../assets/images/tg.png';
import { trackGoal, trackEvent, GOALS } from '../../utils/yandexMetrika';
import LegalNotice from '../LegalNotice/LegalNotice';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const handlePhoneClick = (office) => {
    trackGoal(GOALS.PHONE_CLICK, { phone_location: `footer_${office}` });
    trackEvent('Contact', 'Phone Click', `Footer ${office}`);
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    
    if (isHomePage) {
      const section = document.getElementById(sectionId);
      if (section) {
        const headerHeight = 80;
        const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-top">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-column">
              <h3 className="footer-title">МИГРАНТ PRO</h3>
              <p className="footer-description">
                Профессиональная помощь в решении миграционных вопросов в Москве и Московской области.
              </p>
              <div className="social-links">
                <a 
                  href="https://wa.me/79257166607" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link"
                  onClick={() => {
                    trackGoal(GOALS.WHATSAPP_CLICK, { location: 'footer' });
                    trackEvent('Contact', 'WhatsApp Click', 'Footer');
                  }}
                >
                  <img src={whatsappIcon} alt="WhatsApp" />
                </a>
                <a 
                  href="https://t.me/migrant_pro_1/646" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link"
                  onClick={() => trackEvent('Contact', 'Telegram Click', 'Footer')}
                >
                  <img src={telegramIcon} alt="Telegram" />
                </a>
              </div>
            </div>

            <div className="footer-column">
              <h4 className="footer-subtitle">Навигация</h4>
              <ul className="footer-links">
                <li>
                  <a 
                    href="/#services"
                    onClick={(e) => handleNavClick(e, 'services')}
                  >
                    Услуги
                  </a>
                </li>
                <li>
                  <a 
                    href="/#reviews"
                    onClick={(e) => handleNavClick(e, 'reviews')}
                  >
                    Отзывы
                  </a>
                </li>
                <li>
                  <a 
                    href="#footer-contacts"
                    onClick={(e) => {
                      e.preventDefault();
                      const contacts = document.getElementById('footer-contacts');
                      if (contacts) {
                        contacts.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Контакты
                  </a>
                </li>
                <li>
                  <Link to="/privacy">Политика конфиденциальности</Link>
                </li>
              </ul>
            </div>

            <div className="footer-column" id="footer-contacts">
              <h4 className="footer-subtitle">Контакты</h4>
              <div className="footer-contacts">
                <div className="contact-item office-section">
                  <MapPin size={18} />
                  <div>
                    <p className="contact-label">Офис в Московском:</p>
                    <p className="address-text">г. Москва, г. Московский, 1-й мкр., д.39, к.1 (2 этаж)</p>
                    <div className="phone-wrapper">
                      <Phone size={16} />
                      <a 
                        href="tel:+79252461666" 
                        onClick={() => handlePhoneClick('moscow')}
                      >
                        +7 (925) 246-16-66
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="contact-item office-section">
                  <MapPin size={18} />
                  <div>
                    <p className="contact-label">Офис в Подольске:</p>
                    <p className="address-text">Московская область, Подольск, пр-д Авиаторов, д. 1 ТЦ "МОЛОТОК", 2 этаж, павильон А-59</p>
                    <div className="phone-wrapper">
                      <Phone size={16} />
                      <a 
                        href="tel:+79257166607"
                        onClick={() => handlePhoneClick('podolsk')}
                      >
                        +7 (925) 716-66-07
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-item office-section">
                  <MapPin size={18} />
                  <div>
                    <p className="contact-label">Офис в Москве:</p>
                    <p className="address-text">г. Москва, МКАД, 34км, влд. 6</p>
                    <div className="phone-wrapper">
                      <Phone size={16} />
                      <a 
                        href="tel:+79259146667"
                        onClick={() => handlePhoneClick('podolsk')}
                      >
                        +7 (925) 914-66-67
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="contact-item office-section" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <Mail size={18} style={{ color: 'var(--primary-blue)' }} />
                    <p className="contact-label" style={{ margin: 0 }}>Связь и режим работы</p>
                  </div>
                  <div style={{ paddingLeft: '2.25rem' }}>
                    <div className="phone-wrapper" style={{ marginTop: 0, paddingTop: 0, border: 'none' }}>
                      <a 
                        href="mailto:9252461666@ya.ru"
                        onClick={() => {
                          trackGoal(GOALS.EMAIL_CLICK, { location: 'footer' });
                          trackEvent('Contact', 'Email Click', 'Footer');
                        }}
                      >
                        9252461666@ya.ru
                      </a>
                    </div>
                    <div className="phone-wrapper" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(15,23,42,0.1)' }}>
                      <Clock size={16} />
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem', color: 'var(--text-gray)' }}>Пн - Чт: 09:00—18:00</p>
                        <p style={{ margin: '0 0 4px 0', fontSize: '0.9rem', color: 'var(--text-gray)' }}>Пт: 09:00—17:00</p>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-gray)' }}>Сб, Вс — выходной</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <LegalNotice className="footer-legal-notice" />
          <div className="footer-bottom-content">
            <p>© {currentYear} Мигрант PRO. Все права защищены.</p>
            <p className="footer-inn">ИП Загибалова М. А. · ИНН 500403234260 · ОГРНИП 325508100115316</p>
            <p className="footer-age">18+</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
